import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, Plus, X } from "lucide-react";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { createInterpretationTheme } from "./api";
import Badge from "./components/ui/Badge";
import BadgeBrainRegion from "./components/ui/BadgeBrainRegion";
import Button from "./components/ui/Button";
import Card from "./components/ui/Card";
import Input from "./components/ui/Input";
import Select from "./components/ui/Select";
import Textarea from "./components/ui/Textarea";
import { BrainRegionText } from "./DiagnosticsPatternDetails";
import { getUniqueBrainAreasFromChannels } from "./lib/utils";
import BaseTemplate from "./templates/BaseTemplate";

const Threshold = ({
  index,
  channel,
  band,
  type,
  threshold,
  handleRemoveThreshold,
}) => {
  return (
    <div className="flex flex-row gap-2 justify-between items-center p-3 text-sm bg-white rounded-xl shadow-md border border-gray-200">
      <span>
        <BrainRegionText key={channel} channel={channel}>
          {`${channel} `}
        </BrainRegionText>
      </span>
      <span>{band}</span>
      <span>{type}</span>
      <span>{threshold}</span>
      <button
        onClick={() => handleRemoveThreshold(index)}
        className="cursor-pointer"
        type="button"
      >
        <X size={"16"} />
      </button>
    </div>
  );
};

const InterpretationThemeAdd = () => {
  const [newTheme, setNewTheme] = useState({
    interpretation_text: "",
    keyword: "",
    source_type: "",
    cited_references: "",
    theme_involved_channels: "",
    theme_involved_bands: "",
    theme_deviation_types: "",
  });
  const [channels, setChannels] = useState([]);
  const [bands, setBands] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [deviationTypes, setDeviationTypes] = useState([]); // ['AbsolutePower', 'RelativePower']
  const [newThreshold, setNewThreshold] = useState({
    channel: "",
    band: "",
    type: "amplitude",
    operator: ">=",
    value: "",
  });
  const [thresholds, setThresholds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  let navigate = useNavigate();

  const brainAreas = getUniqueBrainAreasFromChannels(channels ?? []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTheme((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChangeThreshold = (e) => {
    const { name, value } = e.target;
    setNewThreshold((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddInterpretationTheme = async (e) => {
    e.preventDefault();

    if (!newTheme.interpretation_text) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const themeToCreate = {
        ...newTheme,
        keywords: keywords,
        source_type: "ClinicianJudgment",
        cited_references: "",
        theme_involved_channels: channels,
        theme_involved_bands: bands,
        theme_deviation_types: deviationTypes,
        thresholds: thresholds,
      };

      console.log(themeToCreate);

      await createInterpretationTheme(themeToCreate);
      setNewTheme({
        interpretation_text: "",
        keywords: "",
        source_type: "",
        cited_references: "",
        theme_involved_channels: "",
        theme_involved_bands: "",
        theme_deviation_types: "",
      });
      setKeywords([]);
      setChannels([]);
      setBands([]);
      setThresholds([]);
      setDeviationTypes([]);
      navigate("/configuration");
    } catch (err) {
      console.error("Error creating interpretation theme:", err);
      setError(err.response?.data?.detail || err.message);
    }
    setIsLoading(false);
  };

  const handleAddKeyword = (e) => {
    e.preventDefault();
    const keyword = newTheme.keyword.trim();
    const isNotUnique = keywords.includes(keyword);

    if (!keyword || isNotUnique) {
      return;
    }

    setKeywords((v) => [...v, keyword]);
    setNewTheme((v) => ({
      ...v,
      keyword: "",
    }));
  };

  const handleAddThreshold = (e) => {
    e.preventDefault();
    if (!newThreshold.channel || !newThreshold.band || !newThreshold.type || !newThreshold.value) {
      return;
    }
    const isRange = /\d+(?:\.\d+)?\s*-\s*\d+(?:\.\d+)?/.test(newThreshold.value);
    const thresholdStr = isRange
      ? newThreshold.value.replace(/\s+/g, "")
      : `${newThreshold.operator}${newThreshold.value}`;

    setThresholds((v) => [
      ...v,
      {
        channel: newThreshold.channel,
        band: newThreshold.band,
        type: newThreshold.type,
        threshold: thresholdStr,
      },
    ]);

    setNewThreshold((v) => ({
      channel: "",
      band: "",
      type: "amplitude",
      operator: ">=",
      value: "",
    }));
  };

  const handleRemoveKeyword = (keyword) => {
    const newKeywords = keywords.filter((v) => v !== keyword);
    setKeywords(newKeywords);
  };

  const handleRemoveThreshold = (index) => {
    const newThresholds = thresholds.filter((v, idx) => idx !== index);
    setThresholds(newThresholds);
  };

  const handleAddChannel = (e) => {
    e.preventDefault();
    const channel = newTheme.theme_involved_channels.trim();
    const isNotUnique = channels.includes(channel);

    if (!channel || isNotUnique) {
      return;
    }

    setChannels((v) => [...v, channel]);
    setNewTheme((v) => ({
      ...v,
      theme_involved_channels: "",
    }));
  };

  const handleRemoveChannel = (channel) => {
    const newChannels = channels.filter((v) => v !== channel);
    setChannels(newChannels);
  };

  const handleAddBand = (e) => {
    e.preventDefault();
    const band = newTheme.theme_involved_bands.trim();
    const isNotUnique = bands.includes(band);

    if (!band || isNotUnique) {
      return;
    }

    setBands((v) => [...v, band]);
    setNewTheme((v) => ({
      ...v,
      theme_involved_bands: "",
    }));
  };

  const handleRemoveBand = (band) => {
    const newBands = bands.filter((v) => v !== band);
    setBands(newBands);
  };

  return (
    <BaseTemplate>
      <AnimatePresence>
        <Card name={"Add Interpretation Theme"} className={""}>
          <div className="flex flex-col gap-4">
            <div className="flex">
              <NavLink
                to={"/configuration"}
                className={
                  "flex flex-row items-center text-sm text-gray-500 hover:text-gray-400 focus-visible:text-gray-400 transition-[color,translate] hover:translate-x-2 focus-visible:translate-x-2"
                }
              >
                <ChevronLeft size={"16"} />
                <span>Back to Diagnostics Center</span>
              </NavLink>
            </div>
            <Card name={"Interpretation Theme Details"} variant="secondary">
              <div className="flex flex-col gap-4">
                <Textarea
                  label="Description"
                  onChange={handleInputChange}
                  name="interpretation_text"
                  value={newTheme.interpretation_text}
                  placeholder="Description of the theme"
                  required
                  rows={2}
                  className="text-sm"
                />
              </div>
            </Card>

            <Card name={"Channels and Bands"} variant="secondary">
              <div className="flex flex-col lg:flex-row gap-4">
                <form
                  onSubmit={handleAddChannel}
                  className="flex flex-col gap-2 w-full"
                >
                  <span className="text-base">Channels</span>
                  {channels.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {channels?.map((channel) => (
                        <BrainRegionText
                          key={channel}
                          channel={channel}
                          coloredBackground={true}
                          className={"flex items-center gap-1"}
                        >
                          {channel}
                          <button
                            onClick={() => handleRemoveChannel(channel)}
                            className="cursor-pointer"
                            type="button"
                          >
                            <X size={"16"} />
                          </button>
                        </BrainRegionText>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      onChange={handleInputChange}
                      name="theme_involved_channels"
                      value={newTheme.theme_involved_channels}
                      placeholder="F3,F4"
                      required
                      className={"w-full"}
                    />
                    <Button
                      type="submit"
                      className="text-white px-3 py-1 md:px-4 md:py-1.5"
                    >
                      <Plus />
                    </Button>
                  </div>
                </form>
                <form
                  onSubmit={handleAddBand}
                  className="flex flex-col gap-2 w-full"
                >
                  <span className="text-base">Bands</span>
                  {bands.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {bands.map((band) => (
                        <Badge key={band} className={"flex items-center gap-1"}>
                          {band}
                          <button
                            onClick={() => handleRemoveBand(band)}
                            className="cursor-pointer"
                            type="button"
                          >
                            <X size={"16"} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      onChange={handleInputChange}
                      name="theme_involved_bands"
                      value={newTheme.theme_involved_bands}
                      placeholder="delta,theta"
                      required
                      className={"w-full"}
                    />
                    <Button
                      type="submit"
                      className="text-white px-3 py-1 md:px-4 md:py-1.5"
                    >
                      <Plus />
                    </Button>
                  </div>
                </form>
              </div>
            </Card>
            <Card name={"Involved Brain Areas"} variant="secondary">
              {brainAreas.length > 0 ? (
                <div className="flex flex-row gap-2">
                  {brainAreas.map((brainArea) => (
                    <BadgeBrainRegion key={brainArea} region={brainArea} />
                  ))}
                </div>
              ) : (
                <span>No involved brain areas</span>
              )}
            </Card>
            <div className="flex flex-col lg:flex-row gap-4">
              <Card
                name={"Thresholds"}
                variant="secondary"
                scroll={false}
                className={"w-full"}
              >
                <div className="flex flex-col gap-2">
                  {thresholds &&
                    thresholds.map((threshold, index) => (
                      <Threshold
                        key={index}
                        index={index}
                        channel={threshold.channel}
                        band={threshold.band}
                        type={threshold.type}
                        threshold={threshold.threshold}
                        handleRemoveThreshold={handleRemoveThreshold}
                      />
                    ))}
                  <form
                    onSubmit={handleAddThreshold}
                    className="flex flex-col gap-2"
                  >
                    <div className="flex flex-row gap-2">
                      <Input
                        type="text"
                        onChange={handleInputChangeThreshold}
                        name="channel"
                        value={newThreshold.channel}
                        placeholder="channel e.g. F3"
                        required
                        className="w-full"
                      />
                      <Input
                        type="text"
                        onChange={handleInputChangeThreshold}
                        name="band"
                        value={newThreshold.band}
                        placeholder="band e.g. delta"
                        required
                        className="w-full"
                      />
                      <Select
                        name="type"
                        defaultValue={newThreshold.type}
                        options={[
                          { value: "amplitude", label: "Amplitude" },
                          { value: "percentage", label: "Percentage" },
                        ]}
                        onChange={handleInputChangeThreshold}
                      />
                      <Select
                        name="operator"
                        defaultValue={newThreshold.operator}
                        options={[
                          { value: "<", label: "<" },
                          { value: "<=", label: "<=" },
                          { value: ">", label: ">" },
                          { value: ">=", label: ">=" },
                        ]}
                        onChange={handleInputChangeThreshold}
                      />
                      <Input
                        type="text"
                        onChange={handleInputChangeThreshold}
                        name="value"
                        value={newThreshold.value}
                        placeholder="value (e.g. 3.5 or 1.5-3.0)"
                        required
                        className="w-full"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="text-white px-3 py-1 md:px-4 md:py-1.5"
                    >
                      <Plus />
                      Add Threshold
                    </Button>
                  </form>
                </div>
              </Card>
              <Card name={"Keywords"} variant="secondary" className={"w-full"}>
                <form
                  onSubmit={handleAddKeyword}
                  className="flex flex-col gap-2"
                >
                  {keywords && keywords.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {keywords?.map((keyword) => (
                        <Badge key={keyword}>
                          <div className="flex gap-1">
                            {keyword}
                            <button
                              onClick={() => handleRemoveKeyword(keyword)}
                              className="cursor-pointer"
                              type="button"
                            >
                              <X size={"16"} />
                            </button>
                          </div>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Input
                    type="text"
                    onChange={handleInputChange}
                    name="keyword"
                    value={newTheme.keyword}
                    placeholder="e.g. anxiety,executive function"
                    className="w-full"
                  />
                  <Button
                    type="submit"
                    className="text-white px-3 py-1 md:px-4 md:py-1.5"
                  >
                    <Plus />
                    Add Keyword
                  </Button>
                </form>
              </Card>
            </div>
            <Card name={"Measure Types"} variant="secondary">
              <div className="flex flex-col gap-2">
                <p className="text-sm text-gray-600">
                  Select which measure types this theme applies to. Leave empty to apply to both.
                </p>
                <div className="flex flex-row gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deviationTypes.includes("AbsolutePower")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDeviationTypes((prev) => [...prev, "AbsolutePower"]);
                        } else {
                          setDeviationTypes((prev) =>
                            prev.filter((t) => t !== "AbsolutePower")
                          );
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm">Amplitude (AbsolutePower)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deviationTypes.includes("RelativePower")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDeviationTypes((prev) => [...prev, "RelativePower"]);
                        } else {
                          setDeviationTypes((prev) =>
                            prev.filter((t) => t !== "RelativePower")
                          );
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm">Percentage (RelativePower)</span>
                  </label>
                </div>
                {deviationTypes.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {deviationTypes.map((type) => (
                      <Badge key={type} className="flex items-center gap-1">
                        {type === "AbsolutePower" ? "Amplitude" : "Percentage"}
                        <button
                          onClick={() =>
                            setDeviationTypes((prev) =>
                              prev.filter((t) => t !== type)
                            )
                          }
                          className="cursor-pointer"
                          type="button"
                        >
                          <X size={"16"} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </Card>
            <div className="flex space-x-2 pt-1 md:pt-2">
              <Button
                disabled={isLoading}
                className={`px-3 py-1.5 md:px-5 md:py-2 bg-gradient-to-r from-[#2a5caa] to-[#3963aa] text-white rounded-lg text-xs md:text-sm font-medium shadow-sm hover:shadow-md transition-all ${
                  isLoading ? "opacity-70 cursor-not-allowed" : ""
                } ml-auto`}
                onClick={handleAddInterpretationTheme}
              >
                {isLoading ? "Creating..." : "Create Theme"}
              </Button>
            </div>
            {error && (
              <motion.div
                className="p-2 md:p-3 bg-red-50 border border-red-200 rounded-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <p className="text-red-600 text-xs md:text-sm">
                  {JSON.stringify(error)}
                </p>
              </motion.div>
            )}
          </div>
        </Card>
      </AnimatePresence>
    </BaseTemplate>
  );
};

export default InterpretationThemeAdd;
